import { useTextTranslation } from "../../utils/useTextTranslation";
import { isComingSoon, publicationLabel } from "../../utils/publication";
import { openVodDetail } from "../newVodDetail/vodModalNavigation";
import React, {
  useContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import SinglePlayIcon from "../../assets/icons/svg/SinglePlayIcon";
import localizationStrings from "../../statics/localization";
import { fancyTimeFormat } from "../../utils/fancyTimeFormat";
import { SessionContext } from "../../context/SessionContext";
import { useHistory, useLocation } from "react-router-dom";
import LikeAction from "../likeAction";
import FavoriteAction from "../favoriteAction";
import RtukAgeIcon from "../../assets/icons/svg/RtukAgeIcon";
import RtukGeneralIcon from "../../assets/icons/svg/RtukGeneralIcon";
import RtukSevenAgeIcon from "../../assets/icons/svg/RtukSevenAgeIcon";
import RtukThirteenAgeIcon from "../../assets/icons/svg/RtukThirteenAgeIcon";
import getParentalAdvisoryElements from "../../utils/getParentalAdvisory";
import { createPortal } from "react-dom";
import usePreviewPlacement from "./usePreviewPlacement";
import TrailerPlayer from "../trailerPlayer";
import TrailerAudioOnIcon from "../../assets/icons/svg/TrailerAudioOnIcon";
import AudioOffIcon from "../../assets/icons/svg/AudioOffIcon";
import {
  setTrailerAudioOnGlobal,
  getTrailerAudioOnGlobal
} from "../../statics/config";
import mainLoadingGif from "../../assets/loading.gif";
import { getTrailerPlayUrlById, getContentById } from "../../api/vod/vod";
import { prettyTimeFormatter } from "../../utils/prettyTimeFormatter";
import { getPosterUrlByType } from "../../utils/findPoster";
import { useCatalogTheme } from "../cinema/CatalogTheme";
import { getKidsCopy } from "../cinema/kidsTheme";
import CinemaIcon from "../cinema/CinemaIcon";

const MiniVodDetail = ({ closeModal, vodData }) => {
  const translateText = useTextTranslation();
  const { isKids } = useCatalogTheme();
  const [hasScale, setHasScale] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [trailerPlayUrl, setTrailerPlayUrl] = useState(null);
  const [canWatch, setCanWatch] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(getTrailerAudioOnGlobal()); //trailer player sound
  const [showLoading, setShowLoading] = useState(false);
  const [vodItem, setVodItem] = useState(null);
  const [bookmarkedEpisode, setBookmarkedEpisode] = useState(null);
  const [bookmarkedSeason, setBookmarkedSeason] = useState(null);
  const [bookmarkTime, setBookmarkTime] = useState(0);

  const { path, setShowLoginMessage, hasLoggedIn } = useContext(SessionContext);
  const history = useHistory();
  const location = useLocation();
  const modalRef = useRef(null);
  const loadingTimeoutId = useRef(null);
  const openTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const trailerTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const closingRef = useRef(false);

  const closeMiniVod = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    clearTimeout(openTimeoutRef.current);
    clearTimeout(trailerTimeoutRef.current);
    setTrailerPlayUrl(null);
    setHasScale(false);
    closeTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      closeModal();
    }, 200);
  }, [closeModal]);

  usePreviewPlacement(
    modalRef,
    vodData,
    !!vodItem,
    closeMiniVod,
    isKids ? 480 : undefined
  );

  useEffect(() => {
    const handleMouseMove = (event) => {
      // The anchor remains a valid hover target when the preview shifts to fit.
      if (
        !modalRef.current?.contains(event.target) &&
        !vodData.anchorElement?.contains(event.target)
      )
        closeMiniVod();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMiniVod();
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [vodData, closeMiniVod]);

  const calcDuration = () => {
    if (vodItem?.type === "MOVIE") {
      return prettyTimeFormatter(vodItem?.duration);
    } else if (vodItem?.type === "SERIES") {
      return `${vodItem?.children?.length + " " + localizationStrings.season}`;
    }
  };

  const onClickVod = (isPlay = false) => {
    let item = vodData.vod;
    openVodDetail(history, item?.id, isPlay ? "?play=1" : "");

    loadingTimeoutId.current && clearTimeout(loadingTimeoutId.current);
    loadingTimeoutId.current = setTimeout(() => {
      setShowLoading(false);
      closeModal();
    }, 2000);
  };

  const getVodItem = async (vodId) => {
    const data = await getContentById(vodId);
    if (!mountedRef.current || closingRef.current) return;
    const vodInfo = data?.data;
    if (!vodInfo?.id || typeof vodInfo.title !== "string") {
      throw new Error("Invalid preview response");
    }
    getTrailerUrl(vodId);
    setVodItem(vodInfo);

    // Set canWatch based on content authorization
    setCanWatch(vodInfo?.isUnlocked !== false);

    // Handle bookmark information for series and movies
    if (vodInfo?.type === "SERIES") {
      // Find the last watched episode and season
      if (vodInfo?.children && vodInfo.children.length > 0) {
        // Look for bookmark information in the content
        const lastContentId = vodInfo?.interactions?.find(
          (interaction) => interaction.type === "VOD_WATCHED"
        )?.lastContentId;

        if (lastContentId) {
          // Find the season and episode that matches the last content ID
          for (const season of vodInfo.children) {
            if (season.children && season.children.length > 0) {
              const episode = season.children.find(
                (ep) => ep.id === lastContentId
              );
              if (episode) {
                // Check if the episode is completed (watched more than 95% or within last 30 seconds)
                const isEpisodeCompleted =
                  episode.lastDuration &&
                  episode.duration &&
                  (episode.lastDuration >= episode.duration * 0.95 ||
                    episode.duration - episode.lastDuration <= 30);

                if (isEpisodeCompleted) {
                  // Find next episode
                  const episodeIndex = season.children.findIndex(
                    (ep) => ep.id === lastContentId
                  );
                  if (
                    episodeIndex >= 0 &&
                    episodeIndex < season.children.length - 1
                  ) {
                    // Next episode in same season
                    const nextEpisode = season.children[episodeIndex + 1];
                    setBookmarkedEpisode(nextEpisode);
                    setBookmarkedSeason(season);
                    setBookmarkTime(0); // Start from beginning
                  } else {
                    // Look for next season
                    const seasonIndex = vodInfo.children.findIndex(
                      (s) => s.id === season.id
                    );
                    if (
                      seasonIndex >= 0 &&
                      seasonIndex < vodInfo.children.length - 1
                    ) {
                      const nextSeason = vodInfo.children[seasonIndex + 1];
                      if (
                        nextSeason.children &&
                        nextSeason.children.length > 0
                      ) {
                        setBookmarkedEpisode(nextSeason.children[0]);
                        setBookmarkedSeason(nextSeason);
                        setBookmarkTime(0); // Start from beginning
                      } else {
                        // Current episode is the last available, continue with current
                        setBookmarkedEpisode(episode);
                        setBookmarkedSeason(season);
                        setBookmarkTime(episode.lastDuration || 0);
                      }
                    } else {
                      // This is the last episode of the last season
                      setBookmarkedEpisode(episode);
                      setBookmarkedSeason(season);
                      setBookmarkTime(episode.lastDuration || 0);
                    }
                  }
                } else {
                  // Episode not completed, continue from where left off
                  setBookmarkedEpisode(episode);
                  setBookmarkedSeason(season);
                  setBookmarkTime(episode.lastDuration || 0);
                }
                break;
              }
            }
          }
        }
      }
    } else if (vodInfo?.type === "MOVIE") {
      // For movies, check if there's a bookmark time
      const vodContinue = vodInfo?.interactions?.find(
        (interaction) => interaction.type === "VOD_WATCHED"
      );
      if (vodContinue?.lastDuration) {
        setBookmarkTime(vodContinue.lastDuration);
      }
    }
  };

  const playContent = async () => {
    if (!vodItem) return;

    let item = vodData.vod;
    let playUrl = `/dashboard/voddetail/${item?.id}?play=1`;

    // Add bookmark information to URL for proper continuation
    if (vodItem.type === "SERIES" && bookmarkedEpisode) {
      // For series, include the episode and season information
      playUrl += `&episodeId=${bookmarkedEpisode.id}&seasonId=${bookmarkedSeason.id}`;
      if (bookmarkTime > 0) {
        playUrl += `&bookmarkTime=${bookmarkTime}`;
      }
    } else if (vodItem.type === "MOVIE" && bookmarkTime > 0) {
      // For movies, include the bookmark time
      playUrl += `&bookmarkTime=${bookmarkTime}`;
    }

    // Navigate to the VOD detail page with play parameters
    if (history.location.pathname.startsWith("/dashboard/voddetail/"))
      history.replace(playUrl);
    else history.push(playUrl);

    // Close the modal
    loadingTimeoutId.current && clearTimeout(loadingTimeoutId.current);
    loadingTimeoutId.current = setTimeout(() => {
      setShowLoading(false);
      closeModal();
    }, 2000);
  };

  const getTrailerUrl = async (vodId) => {
    try {
      const { data } = await getTrailerPlayUrlById(vodId);

      if (
        !mountedRef.current ||
        closingRef.current ||
        typeof data !== "string" ||
        !data.trim()
      )
        return;
      trailerTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && !closingRef.current) setTrailerPlayUrl(data);
      }, 500);
    } catch (err) {
      if (mountedRef.current) setTrailerPlayUrl(null);
    }
  };

  useLayoutEffect(() => {
    if (!vodItem || closingRef.current) return;
    setIsVisible(true);
    openTimeoutRef.current = setTimeout(() => setHasScale(true), 10);
    return () => clearTimeout(openTimeoutRef.current);
  }, [vodItem]);

  useEffect(() => {
    if (vodData?.vod?.id) {
      getVodItem(vodData.vod.id).catch(() => {
        if (mountedRef.current) closeMiniVod();
      });
    }
    return () => {
      mountedRef.current = false;
      clearTimeout(openTimeoutRef.current);
      clearTimeout(closeTimeoutRef.current);
      clearTimeout(trailerTimeoutRef.current);
      clearTimeout(loadingTimeoutId.current);
    };
  }, []);

  return createPortal(
    <div
      className={`modal-hover${isKids ? " kids-preview" : ""} ${
        hasScale ? "scale" : ""
      } ${isVisible ? "visible" : ""}`}
      ref={modalRef}
    >
      <div className={`modal-preview-vod`}>
        <div className="mini-vod-detail-container">
          <div className="image-wrapper">
            {trailerPlayUrl ? (
              <TrailerPlayer
                playUrl={trailerPlayUrl}
                poster={getPosterUrlByType(vodItem?.posters, "Web Landscape")}
                isAudioOn={isAudioOn}
                isPlay={isVisible && hasScale}
                onAutoplayMuted={() => {
                  setIsAudioOn(false);
                  setTrailerAudioOnGlobal(false);
                }}
                isFrom="miniVodDetail"
              />
            ) : (
              <img
                className={`vod-preview-img ${hasScale ? "scale" : ""}`}
                src={getPosterUrlByType(vodItem?.posters, "Web Landscape")}
                alt={translateText(vodItem?.title)}
              />
            )}

            {trailerPlayUrl ? (
              <div
                className={`action-button secondary absolute`}
                onClick={() => {
                  setIsAudioOn(!isAudioOn);
                  setTrailerAudioOnGlobal(!isAudioOn);
                }}
              >
                <span className="icon">
                  {isAudioOn ? <TrailerAudioOnIcon /> : <AudioOffIcon />}
                </span>
              </div>
            ) : null}
          </div>
          <div className={`preview-info ${hasScale ? "scale" : ""}`}>
            {isKids && (
              <div className="kids-preview-eyebrow">
                <CinemaIcon name="sparkle" width="16" height="16" />
                {getKidsCopy().previewEyebrow}
              </div>
            )}
            <div className="mini-voddetail-row preview-title-row">
              <div className="vod-title">
                <span>{translateText(vodItem?.title)}</span>
              </div>
              <div className="button-group">
                <FavoriteAction vod={vodItem} newVodDetail />
                <LikeAction vod={vodItem} newVodDetail />
                {isComingSoon(vodItem) ? <p role="status">{publicationLabel(vodItem)}</p> : <button
                  type="button"
                  className="action-button primary"
                  aria-label={localizationStrings.play}
                  disabled={showLoading}
                  onClick={() => {
                    if (!showLoading) {
                      if (hasLoggedIn) {
                        setShowLoading(true);
                        // If content is unlocked, play directly. If locked, go to inspect page
                        if (vodItem?.isUnlocked !== false) {
                          playContent();
                        } else {
                          onClickVod();
                        }
                      } else {
                        setShowLoginMessage(true);
                      }
                    }
                  }}
                  style={{
                    cursor: "pointer"
                  }}
                >
                  <span className="icon">
                    {showLoading ? (
                      <div className="loading-gif">
                        <img src={mainLoadingGif} />
                      </div>
                    ) : (
                      <SinglePlayIcon />
                    )}
                  </span>
                  <span className={`action-label`}>
                    {vodItem?.isUnlocked !== false &&
                    ((bookmarkedEpisode && bookmarkTime > 0) ||
                      (vodItem?.type === "MOVIE" && bookmarkTime > 0))
                      ? localizationStrings.continue
                      : localizationStrings.play}
                  </span>
                </button>}
                {/* <ShareAction vod={vodData?.vod} /> */}
              </div>
            </div>
            <div className="mini-voddetail-row">
              <div
                className="vod-info-wrapper"
                style={{ justifyContent: "flex-start" }}
              >
                <div className="content-indicators">
                  <div className="rating-group">
                    {vodItem?.maturityRating?.age === "G" && (
                      <div className="rating">
                        <RtukGeneralIcon />
                      </div>
                    )}
                    {vodItem?.maturityRating?.age === "7+" && (
                      <div className="rating">
                        <RtukSevenAgeIcon />
                      </div>
                    )}
                    {vodItem?.maturityRating?.age === "13+" && (
                      <div className="rating">
                        <RtukThirteenAgeIcon />
                      </div>
                    )}
                    {vodItem?.maturityRating?.age === "18+" && (
                      <div className="rating">
                        <RtukAgeIcon />
                      </div>
                    )}
                  </div>
                  {Array.isArray(vodItem?.maturityRating?.categories) &&
                  vodItem?.maturityRating?.categories.length > 0 ? (
                    <div className="parental-advisory">
                      {getParentalAdvisoryElements(
                        vodItem?.maturityRating?.categories
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="vod-info">
                  {vodItem?.type === "MOVIE"
                    ? localizationStrings.movie
                    : localizationStrings.series}
                </div>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
                <div className="vod-info">{vodItem?.year}</div>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
                <div className="vod-info">{calcDuration()}</div>
                {/* <div
                  className="vod-info"
                  style={{
                    marginLeft: "auto",
                    display: !vodItem?.imdbRating ? "none" : ""
                  }}
                >
                  <div className="imdb-point">IMDB {vodItem?.imdbRating}</div>
                </div> */}
              </div>
            </div>
            {(!isKids || vodItem?.summaryLong || vodItem?.summary) && (
              <>
                <div className="mini-voddetail-row">
                  <div className="description-title">
                    {localizationStrings.storyline}
                  </div>
                </div>
                <div
                  className="mini-voddetail-row"
                  style={{ marginTop: ".52vw" }}
                >
                  <div className="description" title={translateText(vodItem?.summary)}>
                    {translateText(vodItem?.summaryLong || (isKids ? vodItem?.summary : ""))}
                  </div>
                </div>
              </>
            )}

            <div
              className="mini-voddetail-row"
              style={{ marginBottom: "29px" }}
            >
              <button
                type="button"
                className="inspect-button"
                onClick={() => onClickVod()}
              >
                {localizationStrings.inspect}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MiniVodDetail;
